function getCartDrawerFocusableElements(container) {
  return getFocusableElements(container).filter(
    (el) => !el.closest('[aria-hidden="true"]') && el.getClientRects().length > 0
  );
}

function trapFocusIn(container, elementToFocus = container) {
  const elements = getCartDrawerFocusableElements(container);
  const first = elements[0] || container;
  const last = elements[elements.length - 1] || container;

  removeTrapFocus();

  if (!elements.length && !container.hasAttribute('tabindex')) {
    container.setAttribute('tabindex', '-1');
  }

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    const focusable = getCartDrawerFocusableElements(container);
    const tabFirst = focusable[0] || container;
    const tabLast = focusable[focusable.length - 1] || container;

    if (event.target === tabLast && !event.shiftKey) {
      event.preventDefault();
      tabFirst.focus();
    }

    if ((event.target === container || event.target === tabFirst) && event.shiftKey) {
      event.preventDefault();
      tabLast.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  (elementToFocus || first).focus();
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.infoTrigger = null;

    this.addEventListener('keyup', this.handleKeyUp.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setAttribute('aria-hidden', 'true');
  }

  handleKeyUp(evt) {
    if (evt.code !== 'Escape') return;

    const openInfo = this.querySelector('.cart-drawer-info-panel.is-open');
    if (openInfo) {
      this.closeInfoOverlay(openInfo);
      return;
    }

    if (this.classList.contains('active')) {
      this.close();
    }
  }

  handleClick(event) {
    if (event.target.closest('#CartDrawer-Overlay')) {
      this.close();
      return;
    }

    const closeBtn = event.target.closest('.drawer__close');
    if (closeBtn) {
      event.preventDefault();
      this.close();
      return;
    }

    const trigger = event.target.closest('[data-cart-info-trigger]');
    if (trigger) {
      event.preventDefault();
      const target = trigger.getAttribute('data-cart-info-trigger');
      const panelId =
        target === 'shipping'
          ? '#CartDrawer-ShippingOverlay'
          : target === 'taxes'
            ? '#CartDrawer-TaxesOverlay'
            : null;
      if (panelId) this.openInfoOverlay(panelId, trigger);
      return;
    }

    const closeTarget = event.target.closest('[data-cart-info-close]');
    if (closeTarget) {
      event.preventDefault();
      if (closeTarget.classList.contains('cart-drawer-info-backdrop')) {
        this.querySelectorAll('.cart-drawer-info-panel.is-open').forEach((panel) => this.closeInfoOverlay(panel));
        return;
      }

      const panel = closeTarget.closest('.cart-drawer-info-panel');
      if (panel) this.closeInfoOverlay(panel);
    }
  }

  getInner() {
    return this.querySelector('.drawer__inner');
  }

  getFocusContainer() {
    return this.querySelector('.cart-drawer-info-panel.is-open') || this.getInner();
  }

  trapDrawerFocus(elementToFocus) {
    const container = this.getFocusContainer();
    if (!container) return;

    const defaultFocus =
      elementToFocus ||
      container.querySelector('.cart-drawer-info-panel__close') ||
      container.querySelector('.drawer__close') ||
      getCartDrawerFocusableElements(container)[0] ||
      container;

    trapFocusIn(container, defaultFocus);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.setAttribute('aria-hidden', 'false');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        this.trapDrawerFocus(this.querySelector('.drawer__close'));
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.querySelectorAll('.cart-drawer-info-panel.is-open').forEach((panel) => this.closeInfoOverlay(panel, true));

    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');

    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setInfoBackdropVisible(visible) {
    const backdrop = this.querySelector('.cart-drawer-info-backdrop');
    if (!backdrop) return;

    backdrop.classList.toggle('is-visible', visible);
    backdrop.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  openInfoOverlay(id, trigger) {
    this.querySelectorAll('.cart-drawer-info-panel.is-open').forEach((panel) => this.closeInfoOverlay(panel, true));

    const panel = this.querySelector(id);
    if (!panel) return;

    this.infoTrigger = trigger;
    this.getInner()?.classList.add('is-info-open');
    this.setInfoBackdropVisible(true);
    trigger?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');

      const title = panel.querySelector('[id$="Title"]');
      if (title?.id) panel.setAttribute('aria-labelledby', title.id);

      const closeBtn = panel.querySelector('.cart-drawer-info-panel__close');
      trapFocusIn(panel, closeBtn || panel);
    });
  }

  closeInfoOverlay(panel, skipAnimation = false) {
    if (!panel.classList.contains('is-open')) return;

    const trigger = this.infoTrigger;

    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
    panel.removeAttribute('aria-labelledby');
    trigger?.setAttribute('aria-expanded', 'false');

    const finishClose = () => {
      if (!this.querySelector('.cart-drawer-info-panel.is-open')) {
        this.getInner()?.classList.remove('is-info-open');
        this.setInfoBackdropVisible(false);

        if (this.classList.contains('active')) {
          if (trigger && document.contains(trigger)) {
            trapFocusIn(this.getInner(), trigger);
          } else {
            this.trapDrawerFocus(this.querySelector('.drawer__close'));
          }
        }

        this.infoTrigger = null;
      }
    };

    if (skipAnimation) {
      finishClose();
      return;
    }

    let finished = false;
    const onTransitionEnd = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onTransitionEnd);
      if (!finished) {
        finished = true;
        finishClose();
      }
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    setTimeout(() => {
      if (!finished) {
        finished = true;
        panel.removeEventListener('transitionend', onTransitionEnd);
        finishClose();
      }
    }, 450);
  }

  renderContents(parsedState) {
    if (this.querySelector('.drawer__inner')?.classList.contains('is-empty')) {
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      if (typeof capitalizeCartVariants === 'function') capitalizeCartVariants();
      this.open(this.activeElement);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
